/**
 * Login-Wizard mit pfad-basierter Workspace-Trennung.
 *
 * Statt eines globalen `/login`-Pfads mit In-Page-Picker hat jeder
 * Workspace seine eigene URL — das ist die "maximale Trennung", die das
 * Refactor-Briefing fordert:
 *
 *   `/login/admin`     → Plattform-Admin-Login (Icarus-intern)
 *   `/login/klinik`    → Klinik-Personal-Login
 *   `/login/ambulant`  → Praxis-Personal-Login
 *   `/login/patient`   → Patientenportal-Login (eigener Supabase-Client)
 *   `/login`           → Workspace-Picker, wenn der User nicht weiß wohin
 *
 * Nach erfolgreichem Login wird die effektive Rolle mit dem gewählten
 * Workspace verglichen (`validateWorkspaceMatch`): passt sie nicht,
 * loggen wir den User wieder aus und zeigen einen verständlichen Toast.
 * So kann ein Klinik-Therapeut nicht versehentlich in den Ambulant-
 * Workspace rutschen, nur weil er den falschen Link geklickt hat.
 *
 * Die Sub-Components (Form-Components) bleiben unverändert; ihr State
 * (isRegister) hängt jetzt am Workspace, nicht mehr an einer separaten
 * Mode-Auswahl. Praxis/Klinik-Mode für Signup wird direkt aus der URL
 * abgeleitet.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import type { SettingType } from '@/hooks/useFeatures';
import { useLoginActions } from '@/hooks/useLoginActions';
import {
  type Workspace,
  workspaceFromPath,
  validateWorkspaceMatch,
  getPostLoginRoute,
} from '@/features/post-login-redirect';

import { LoginWorkspacePicker } from '@/components/login/LoginWorkspacePicker';
import { LoginShell } from '@/components/login/LoginShell';
import { FachpersonSigninForm } from '@/components/login/FachpersonSigninForm';
import { FachpersonSignupForm } from '@/components/login/FachpersonSignupForm';
import { PatientSigninForm } from '@/components/login/PatientSigninForm';
import { PatientSignupForm } from '@/components/login/PatientSignupForm';

/** Workspace → Theme-Mode für die Token-Vorschau direkt nach Auswahl. */
function modeForWorkspace(workspace: Workspace | null): SettingType | null {
  if (workspace === 'klinik') return 'klinik';
  if (workspace === 'ambulant') return 'praxis';
  return null;
}

/** Subtitle unter dem Logo, abhängig vom gewählten Workspace. */
function titleForWorkspace(workspace: Workspace, isRegister: boolean): string {
  if (workspace === 'admin') return 'Plattform-Administration – Anmelden';
  if (workspace === 'klinik') return isRegister ? 'Icarus D&D registrieren' : 'Icarus D&D – Anmelden';
  if (workspace === 'ambulant') return isRegister ? 'Praxis registrieren' : 'Praxis – Anmelden';
  return isRegister ? 'Patientenkonto erstellen' : 'Patient – Anmelden';
}

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, profile, signOut } = useAuth();

  const workspace = workspaceFromPath(location.pathname);
  // Mode aus Workspace abgeleitet — kein separater Picker mehr.
  const mode = modeForWorkspace(workspace);
  const [isRegister, setIsRegister] = useState(false);

  // Die Bereichs-Akzentfarbe (Token-Vorschau) setzt der AreaProvider
  // automatisch aus dem Login-Pfad (`/login/<workspace>` → `.area-*`).

  const actions = useLoginActions({ mode });

  // Wenn schon eingeloggt: Workspace gegen Rolle abgleichen und weiter.
  useEffect(() => {
    if (!session || !profile) return;
    // Patient-Auth läuft über `supabasePortal` — wenn ein Patient-Account
    // hier auftaucht, hat er kein `profile`, die useEffect-Bedingung greift
    // also nicht. Patient-Redirect macht der PatientSigninForm direkt.
    if (workspace === null) {
      navigate(getPostLoginRoute(profile.role), { replace: true });
      return;
    }
    if (workspace === 'patient') return; // Patient-Pfad ist separat
    const match = validateWorkspaceMatch(workspace, profile.role);
    if (match.ok) {
      navigate(match.route, { replace: true });
    } else {
      toast.error(
        'Dieser Zugang gehört nicht zu diesem Workspace. Bitte über die richtige Login-Seite anmelden.',
      );
      void signOut();
    }
  }, [session, profile, workspace, navigate, signOut]);

  // ── Stufe 1: Workspace-Picker, wenn kein Workspace per URL gewählt ──
  if (workspace === null) {
    return (
      <LoginWorkspacePicker
        onPick={(ws) => navigate(`/login/${ws}`)}
      />
    );
  }

  // ── Stufe 2: konkretes Sign-in / Sign-up-Form pro Workspace ──────────
  // Admin hat keinen Signup-Pfad — Plattform-Admins werden manuell vom
  // Icarus-Team angelegt, nicht selbst registriert.
  const allowSignup = workspace !== 'admin';
  const formRole = workspace === 'patient' ? 'patient' : 'fachperson';

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-green-50/60 via-background to-amber-50/30 p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-3">
          <img
            src="/icarus_logo_600px.webp"
            alt="Icarus Health"
            className="h-10 mx-auto"
            style={{ mixBlendMode: 'multiply' }}
          />
          <div>
            <h1 className="font-serif text-[22px] font-medium leading-[1.15] text-ink">
              Icarus D&D
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {titleForWorkspace(workspace, isRegister)}
            </p>
          </div>
        </div>

        <LoginShell
          role={formRole}
          mode={mode}
          isRegister={isRegister}
          onSwitchMode={allowSignup ? () => setIsRegister((v) => !v) : undefined}
          onChangeRole={() => navigate('/login')}
        >
          {formRole === 'fachperson' && !isRegister && (
            <FachpersonSigninForm
              onSubmit={actions.handleFachpersonSignin}
              onForgotPassword={actions.handleForgotPassword}
            />
          )}
          {formRole === 'fachperson' && isRegister && (
            <FachpersonSignupForm
              mode={mode}
              onSubmit={actions.handleFachpersonSignup}
            />
          )}
          {formRole === 'patient' && !isRegister && (
            <PatientSigninForm onSubmit={actions.handlePatientSignin} />
          )}
          {formRole === 'patient' && isRegister && (
            <PatientSignupForm onSubmit={actions.handlePatientSignup} />
          )}
        </LoginShell>

        <p className="text-center text-xs text-muted-foreground">
          Alle Daten werden DSGVO-konform auf EU-Servern gespeichert.
        </p>

        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-3 h-3" />
            Zurück zur Startseite
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
